const DEFAULT_DOCUMENT_IMAGE_URL = 'https://assets.meltwater.io/flux-illustrations/latest/spot/general/svg/card_image_placeholder_aqua.svg';

/**
 * A representation of a document as returned by Search v2 for use in a
 * content stream.
 *
 * @property {string[]} applicationTags
 * @property {IAuthor[]} authors
 * @property {string} body
 * @property {string} country
 * @property {string|Date} date - ISO formatted string
 * @property {boolean} disableInAppOpening
 * @property {string} documentId
 * @property {GydaDocument[]} dupDocs
 * @property {number} [dupDocsCount]
 * @property {string} embedUrl
 * @property {number} engagement
 * @property {string} hostedContent
 * @property {GydaDocumentImage[]} images
 * @property {string} informationType
 * @property {string} ingress
 * @property {boolean} isHosted
 * @property {boolean} isInternalSharingDisabled
 * @property {boolean} isSentimentModified
 * @property {boolean} isSocialSharingDisabled
 * @property {boolean} isTranslateDisabled
 * @property {string[]} keyPhrases
 * @property {string[]} keywords
 * @property {string} language
 * @property {object} mainAuthor
 * @property {boolean} mainAuthor.verifiedAccount
 * @property {string} mainAuthor.verifiedType
 * @property {ICustomCategory[]} matchQueryTags
 * @property {string} matchSentence
 * @property {string} mediaImageUrl
 * @property {string} mediaType
 * @property {IEngagementMetrics} metrics
 * @property {string} originalSentiment - One of `N`, `P`, `V`, `U` for neutral, positive, negative and unrated respectively,
 * @property {string} originalUrl
 * @property {string} paywallUrl
 * @property {object} potentialReach
 * @property {number} potentialReach.desktop
 * @property {number} potentialReach.mobile
 * @property {string} [protectedUrl]
 * @property {string} provider
 * @property {number} reach
 * @property {string} redirectUrl
 * @property {string} sentiment - One of `N`, `P`, `V`, `U` for neutral, positive, negative and unrated respectively,
 * @property {number} socialEcho
 * @property {string} source
 * @property {object} sourceAuthority
 * @property {number} sourceAuthority.newsguardScore
 * @property {string} sourceId
 * @property {string} sourceUrl
 * @property {string} subMediaType
 * @property {string[]} tags
 * @property {string} title
 * @property {string} url
 */
export class GydaDocument {
    constructor({
        applicationTags, //: string[];
        authors, //: IAuthor[];
        body, //: string;
        country, //: string;
        date, //: string; // | Date; // ISO
        disableInAppOpening, //: boolean;
        documentId, //: string;
        dupDocs, //: IDocument[];
        dupDocsCount, //?: number;
        embedUrl, //: string;
        engagement, //: number;
        hostedContent, //: string;
        images, //: { name: string; link: string; mimeType?: string }[];
        informationType, //: string;
        ingress, //: string;
        isHosted, //: boolean;
        isInternalSharingDisabled, //: boolean;
        isSentimentModified, //: boolean;
        isSocialSharingDisabled, //: boolean;
        isTranslateDisabled, //: boolean;
        keyPhrases, //: string[];
        keywords, //: string[];
        language, //: string;
        mainAuthor, //: {
        //   verifiedAccount, // boolean;
        //   verifiedType, // string;
        // },
        matchQueryTags, // ICustomCategory[];
        matchSentence, // string;
        mediaImageUrl, // string;
        mediaType, // string;
        metrics, // IEngagementMetrics;
        originalSentiment, // TSentiment;
        originalUrl, // string;
        paywallUrl, // string;
        potentialReach, //: {
        //   desktop,// number;
        //   mobile,// number;
        // },
        protectedUrl, //?: string;
        provider, // string;
        reach, // number;
        redirectUrl, // string;
        sentiment, // TSentiment;
        socialEcho, //: number;
        source, // string;
        sourceAuthority, //: {
        //   newsguardScore, // number;
        // },
        sourceId,// string;
        sourceUrl,// string;
        subMediaType,// string;
        tags,// string[];
        title,// string;
        url // string;
    } = {}) {
        this.applicationTags = applicationTags;
        this.authors = authors;
        this.body = body;
        this.country = country;
        this.date = date;
        this.disableInAppOpening = disableInAppOpening;
        this.documentId = documentId;
        this.dupDocs = dupDocs;
        this.dupDocsCount = dupDocsCount;
        this.embedUrl = embedUrl;
        this.engagement = engagement;
        this.hostedContent = hostedContent;

        if (images === undefined || images.length === 0) {
            if (mediaImageUrl) {
                this.images = [{
                    name: 'default',
                    link: mediaImageUrl
                }];
            }
            this.images = [{
                name: 'default',
                link: DEFAULT_DOCUMENT_IMAGE_URL
            }];
        } else {
            this.images = images;
        }

        this.informationType = informationType;
        this.ingress = ingress;
        this.isHosted = isHosted;
        this.isInternalSharingDisabled = isInternalSharingDisabled;
        this.isSentimentModified = isSentimentModified;
        this.isSocialSharingDisabled = isSocialSharingDisabled;
        this.isTranslateDisabled = isTranslateDisabled;
        this.keyPhrases = keyPhrases;
        this.keywords = keywords;
        this.language = language;
        this.mainAuthor = mainAuthor;
        this.matchQueryTags = matchQueryTags;
        this.matchSentence = matchSentence;
        this.mediaImageUrl = mediaImageUrl;
        this.mediaType = mediaType;
        this.metrics = metrics;
        this.originalSentiment = originalSentiment;
        this.originalUrl = originalUrl;
        this.paywallUrl = paywallUrl;
        this.potentialReach = potentialReach;
        this.protectedUrl = protectedUrl;
        this.provider = provider;
        this.reach = reach;
        this.redirectUrl = redirectUrl;
        this.sentiment = sentiment ?? 'u';
        this.socialEcho = socialEcho;
        this.source = source;
        this.sourceAuthority = sourceAuthority;
        this.sourceId = sourceId;
        this.sourceUrl = sourceUrl;
        this.subMediaType = subMediaType;
        this.tags = tags;
        this.title = title;
        this.url = url;

        Object.freeze(this);
    }
}

/**
 * @typedef {object} IAuthor
 */

/**
 * @typedef {object} ICustomCategory
 */

/**
 * @typedef {object} IEngagementMetrics
 */

/**
 * @typedef {object} GydaDocumentImage
 * @property {string} link
 * @property {string} mimeType
 * @property {string} name
 */

